/**
 * ConnectionsPage (Phase 2, §7.5) — the Bankverbindungen overview.
 *
 * Lists every bank connection as a `ConnectionCard`, links to the setup wizard,
 * and shows the recent sync-run health log. When no EB credential is stored yet
 * it nudges the user into the wizard's step 1.
 */
import { useNavigate } from 'react-router';
import { Landmark, Plus } from 'lucide-react';
import { PageHeader } from '../../components/layout/PageHeader.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { EmptyState } from '../../components/ui/EmptyState.tsx';
import { ErrorState } from '../../components/ui/ErrorState.tsx';
import { SkeletonList } from '../../components/ui/Skeleton.tsx';
import { useConnections } from '../../lib/hooks/useConnections.ts';
import { useProviderCredential } from '../../lib/hooks/useProviderCredential.ts';
import { ConnectionCard } from './components/ConnectionCard.tsx';
import { SyncHealthPanel } from './SyncHealthPanel.tsx';

const SETUP_PATH = '/bankverbindungen/einrichten';

export default function ConnectionsPage() {
  const navigate = useNavigate();
  const connectionsQuery = useConnections();
  const credentialQuery = useProviderCredential();

  const hasCredential = credentialQuery.data != null;
  const goSetup = () => void navigate(SETUP_PATH);

  return (
    <div>
      <PageHeader
        title="Bankverbindungen"
        subtitle="Automatischer Abgleich über Enable Banking"
        actions={
          <Button size="sm" onClick={goSetup}>
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Bank verbinden</span>
          </Button>
        }
      />

      <div className="w-full space-y-10 px-4 py-6 sm:px-6">
        <section className="space-y-8">
          {connectionsQuery.isPending ? (
            <SkeletonList rows={3} />
          ) : connectionsQuery.isError ? (
            <ErrorState
              error={connectionsQuery.error}
              onRetry={() => void connectionsQuery.refetch()}
            />
          ) : connectionsQuery.data.length === 0 ? (
            <EmptyState
              icon={Landmark}
              title="Noch keine Bankverbindung"
              description={
                hasCredential
                  ? 'Verbinde deine erste Bank, um Umsätze automatisch abzugleichen.'
                  : 'Richte zuerst deine Enable-Banking-App ein, dann verbinde deine Bank.'
              }
              action={
                <Button size="sm" onClick={goSetup}>
                  <Plus className="h-4 w-4" />
                  {hasCredential ? 'Bank verbinden' : 'Einrichten'}
                </Button>
              }
            />
          ) : (
            connectionsQuery.data.map((connection) => (
              <ConnectionCard key={connection.id} connection={connection} />
            ))
          )}
        </section>

        {connectionsQuery.data && connectionsQuery.data.length > 0 ? (
          <section>
            <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500">
              Abgleich-Verlauf
            </h2>
            <SyncHealthPanel limit={20} />
          </section>
        ) : null}
      </div>
    </div>
  );
}
